/* eslint-disable no-unused-vars */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { Canvas } from 'fabric';

/**
 * Менеджер бекапов для системы MetaBullets
 * Управляет созданием бекапов во время операций с метабуллитами
 */
export class MetaBulletBackupManager {
  private canvas: Canvas;
  private canvasId: string;
  private addBackup: (
    canvasId: string,
    state: unknown,
    sendToServer?: boolean,
    generateThumbnail?: boolean,
  ) => void;
  private checkUndoRedoInProgress: () => boolean;

  // Состояние операций
  private currentOperation: MetaBulletOperation | null = null;
  private operationStartTime: number = 0;
  private pendingBackup = false;
  private affectedEntities: Set<string> = new Set();

  constructor(
    canvas: Canvas,
    canvasId: string,
    addBackup: (
      canvasId: string,
      state: unknown,
      sendToServer?: boolean,
      generateThumbnail?: boolean,
    ) => void,
    checkUndoRedoInProgress?: () => boolean,
  ) {
    this.canvas = canvas;
    this.canvasId = canvasId;
    this.addBackup = addBackup;
    this.checkUndoRedoInProgress = checkUndoRedoInProgress || (() => false);
  }

  /**
   * Начать операцию MetaBullet
   */
  startOperation(operation: MetaBulletOperation, entityIds: string[] = []): void {
    this.currentOperation = operation;
    this.operationStartTime = Date.now();
    this.pendingBackup = false;
    this.affectedEntities.clear();

    // Добавляем затронутые сущности
    entityIds.forEach((id) => this.affectedEntities.add(id));

    // Устанавливаем excludeFromBackup для всех затронутых объектов
    this.setExcludeFromBackupForAffectedObjects(true);
  }

  /**
   * Добавить сущность к текущей операции
   */
  addAffectedEntity(entityId: string): void {
    if (this.currentOperation) {
      this.affectedEntities.add(entityId);
      this.setExcludeFromBackupForEntity(entityId, true);
    }
  }

  /**
   * Запросить создание бекапа (будет создан в конце операции)
   */
  requestBackup(): void {
    if (this.currentOperation) {
      this.pendingBackup = true;
    }
  }

  /**
   * Завершить операцию и создать бекап если нужно
   */
  finishOperation(): void {
    if (!this.currentOperation) {
      return;
    }

    const operation = this.currentOperation;

    // Восстанавливаем возможность создания бекапов
    this.setExcludeFromBackupForAffectedObjects(false);

    // Создаем бекап если нужно
    if (this.pendingBackup && this.shouldCreateBackup(operation)) {
      this.createBackup(operation);
    }

    // Очищаем состояние
    this.currentOperation = null;
    this.pendingBackup = false;
    this.affectedEntities.clear();
  }

  /**
   * Принудительно завершить операцию без бекапа (для отмены)
   */
  cancelOperation(): void {
    if (this.currentOperation) {
      // Восстанавливаем возможность создания бекапов
      this.setExcludeFromBackupForAffectedObjects(false);

      // Очищаем состояние без создания бекапа
      this.currentOperation = null;
      this.pendingBackup = false;
      this.affectedEntities.clear();
    }
  }

  /**
   * Проверить активна ли операция
   */
  isOperationActive(): boolean {
    return this.currentOperation !== null;
  }

  /**
   * Получить текущую операцию
   */
  getCurrentOperation(): MetaBulletOperation | null {
    return this.currentOperation;
  }

  /**
   * Установить excludeFromBackup для всех затронутых объектов
   */
  private setExcludeFromBackupForAffectedObjects(exclude: boolean): void {
    this.affectedEntities.forEach((entityId) => {
      this.setExcludeFromBackupForEntity(entityId, exclude);
    });
  }

  /**
   * Установить excludeFromBackup для конкретной сущности
   */
  private setExcludeFromBackupForEntity(entityId: string, exclude: boolean): void {
    const obj = this.canvas
      .getObjects()
      .find((o: unknown) => (o as { id?: string }).id === entityId);
    if (obj) {
      obj.excludeFromBackup = exclude;
    }
  }

  /**
   * Определить нужно ли создавать бекап для операции
   */
  private shouldCreateBackup(operation: MetaBulletOperation): boolean {
    // КРИТИЧНО: Проверяем флаг undo/redo через переданную функцию
    // Во время undo/redo НЕ создаем новые бекапы, чтобы не ломать историю
    const isUndoOrRedoInProgress = this.checkUndoRedoInProgress();

    if (isUndoOrRedoInProgress) {
      return false;
    }

    switch (operation) {
      case 'initialization':
        return true; // Всегда создаем бекап после инициализации (если были изменения)
      case 'entity_create':
        return true; // Создание новой сущности
      case 'entity_delete':
        return true; // Удаление сущности
      case 'marker_delete':
        return true; // Удаление маркера
      case 'entity_drag':
        return true; // Перетаскивание с рекалькуляцией
      case 'entity_type_change':
        return true; // Смена типа/уровня - ВСЕГДА создаем бекап (пользовательское действие)
      case 'text_changed':
        return true; // Изменение текста в существующей сущности
      case 'recalculation':
        return false; // Обычная рекалькуляция без пользовательского действия (спам prevention)
      default:
        return true;
    }
  }

  /**
   * Проверить, является ли операция изменением контента (для флага isChangedContent).
   * Контентные изменения влияют на выбор лейаутов бэкендом (get-layouts/?changed=true).
   */
  private isContentChangingOperation(operation: MetaBulletOperation): boolean {
    switch (operation) {
      case 'text_changed':
      case 'entity_create':
      case 'entity_delete':
      case 'mixed_delete':
      case 'entity_type_change':
        return true;
      default:
        return false;
    }
  }

  /**
   * Создать бекап
   */
  private createBackup(operation: MetaBulletOperation): void {
    try {
      const canvasState = this.canvas.toJSON();

      // Проставляем флаг isChangedContent для операций, меняющих контент слайда.
      // Этот флаг используется при запросе лейаутов (get-layouts/?changed=true/false).
      if (this.isContentChangingOperation(operation)) {
        (canvasState as Record<string, unknown>).isChangedContent = true;
      }

      // КРИТИЧНО: НЕ отправляем на сервер бекапы созданные во время progressive loading
      // (которые могут содержать плейсхолдеры "Loading...")
      // Бекап будет отправлен позже, после завершения загрузки изображений
      const sendToServer = this.currentOperation !== 'initialization';
      const generateThumbnail = this.currentOperation !== 'initialization';

      this.addBackup(this.canvasId, canvasState, sendToServer, generateThumbnail);
    } catch (error) {
      console.error('MetaBulletBackupManager: Error creating backup:', error);
    }
  }
}

/**
 * Типы операций MetaBullet для управления бекапами
 */
export type MetaBulletOperation =
  | 'initialization' // Первая инициализация системы
  | 'entity_create' // Создание новой сущности
  | 'entity_delete' // Удаление сущности
  | 'entity_drag' // Перетаскивание сущности с рекалькуляцией
  | 'marker_delete' // Удаление маркера
  | 'marker_create' // Создание маркера
  | 'mixed_delete' // Смешанное удаление (маркеры + сущности)
  | 'recalculation' // Обычная рекалькуляция позиций (НЕ создает бекап - спам предотвращение)
  | 'style_change' // Изменение стилей
  | 'entity_type_change' // Смена типа/уровня сущности (TAB/Shift+TAB/меню) - ВСЕГДА бекап!
  | 'text_changed'; // Изменение текста в существующей сущности
